// composables/useGeolocation.ts
import { ref, onMounted } from 'vue';
import { useCookie, reloadNuxtApp } from '#app';

/**
 * Composable: useGeolocation
 * 
 * Fetches user location and reloads the Nuxt app only once per defined interval.
 */
export const useGeolocation = () => {
  const coordinates = ref<{ lat: number; lng: number; accuracy?: number } | null>(null);
  const error = ref<string | null>(null);
  const isLoad = ref<Boolean>(false);
  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Reload threshold (in hours)
  const reloadThresholdHours = 1;
  const thresholdMs = reloadThresholdHours * 60 * 60 * 1000;

  // Cookie to persist last reload timestamp
  const lastReload = useCookie<number>('lastReloadTimestamp', {
    default: () => 0
  });


  // The useCookie composable automatically handles getting and setting the value.
  const visitedPlaceIds = useCookie<string[]>('visitedPlaceIds', { default: () => [] });

        
  /**
   * Check and request permission status
   */
  const checkPermission = async () => {
    if (!navigator.permissions) return;
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      if (status.state === 'denied' || status.state === 'prompt') {
        error.value = 'Location permission denied. Enable it in browser settings.';
      }
    } catch (e) {
      console.error('Error checking permissions:', e);
    }
  };

  /**
   * Fetch location and reload app based on threshold
   */
  const fetchLocation = () => {
    console.log('fetchLocation isSupported : ', isSupported);
    
    if (!isSupported) {
      error.value = 'Geolocation not supported';
      return;
    }

    // First, update permission state
    checkPermission();

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        coordinates.value = { lat: latitude, lng: longitude, accuracy };

        // Persist location in cookie
        const locationCookie = useCookie<{ lat: number; lng: number }>('location', {
          default: () => ({ lat: 0, lng: 0 })
        });

        if(locationCookie.value.lat == 0){
          isLoad.value = true
        }

        locationCookie.value = { lat: latitude, lng: longitude };

        // Reload the Nuxt app if threshold has passed
        // const now = Date.now();
        if (latitude && isLoad.value == true) {
          setTimeout(() => {
            console.log('Reloading Nuxt App after threshold...');
            reloadNuxtApp();
          }, 500);
        }
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            error.value = 'Permission denied. Enable location access.';
            break;
          case err.POSITION_UNAVAILABLE:
            error.value = "Position unavailable. Ensure GPS or network is on.";
            break;
          case err.TIMEOUT:
            error.value = 'Request timed out. Try again later.';
            break;
          default:
            error.value = `Unknown error: ${err.message}`;
        }
        console.error(`Geolocation error (${err.code}): ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  // onMounted(fetchLocation);

  return { coordinates, error, fetchLocation, isSupported };
};
